const MIN_COLUMN_WIDTH = 16;
const MAX_COLUMN_WIDTH = 2000;
const ROW_HEIGHTS = [16, 24, 32];
const FONT_SIZES = [10, 11, 12, 13];

const defaultSettings = () => ({ columnWidths: {}, rowHeight: 16, fontSize: 12 });
const validWidth = value => typeof value === 'number' && Number.isFinite(value) &&
  value >= MIN_COLUMN_WIDTH && value <= MAX_COLUMN_WIDTH;

export default {
  props: {
    tableSettingsPage: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      tableSettings: defaultSettings(),
      tableColumnResize: null,
    };
  },
  computed: {
    tableSettingsEnabled() {
      return Boolean(this.tableSettingsPage);
    },
    tableSettingsStorageKey() {
      if (!this.tableSettingsEnabled) return null;
      const profile = this.$store && this.$store.getters['profile/profile'];
      const userId = profile && profile.id;
      if ((typeof userId !== 'string' && typeof userId !== 'number') || userId === '') return null;
      return 'crm.table-settings.' + encodeURIComponent(userId) + '.' + this.tableSettingsPage;
    },
    tableDefaultColumnWidths() {
      const widths = {};
      if (!this.tableSettingsEnabled) return widths;
      (this.fields || []).forEach(field => {
        const key = typeof field === 'string' ? field : field.key;
        const styleWidth = field.thStyle && field.thStyle.width;
        const width = typeof styleWidth === 'number' ? styleWidth :
          (/^\d+(\.\d+)?px$/.test(styleWidth) ? parseFloat(styleWidth) : 120);
        widths[key] = validWidth(width) ? Math.round(width) : 120;
      });
      return widths;
    },
    tableColumnWidths() {
      const widths = { ...this.tableDefaultColumnWidths };
      Object.keys(widths).forEach(key => {
        const saved = this.tableSettings.columnWidths[key];
        if (validWidth(saved)) widths[key] = Math.round(saved);
      });
      return widths;
    },
    tableDisplayFields() {
      if (!this.tableSettingsEnabled) return this.fields;
      return (this.fields || []).map(field => {
        const definition = typeof field === 'string' ? { key: field } : field;
        return {
          ...definition,
          thStyle: { ...definition.thStyle, width: this.tableColumnWidths[definition.key] + 'px' },
        };
      });
    },
    tableSettingsStyle() {
      if (!this.tableSettingsEnabled) return null;
      const width = Object.values(this.tableColumnWidths).reduce((total, value) => total + value, 0);
      return {
        // Сховані зовнішні межі додають один піксель до суми ширин колонок.
        '--table-settings-width': (width + 1) + 'px',
        '--table-settings-row-height': this.tableSettings.rowHeight + 'px',
        '--table-settings-line-height': (this.tableSettings.rowHeight - 1) + 'px',
        '--table-settings-font-size': this.tableSettings.fontSize + 'px',
      };
    },
  },
  watch: {
    tableSettingsStorageKey: {
      immediate: true,
      handler() {
        this.clearTableColumnResize();
        this.loadTableSettings();
      },
    },
  },
  beforeDestroy() {
    if (this.tableColumnResize) this.finishTableColumnResize();
  },
  methods: {
    loadTableSettings() {
      this.tableSettings = defaultSettings();
      if (!this.tableSettingsStorageKey) return;
      try {
        const saved = JSON.parse(window.localStorage.getItem(this.tableSettingsStorageKey));
        if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return;
        const columnWidths = {};
        Object.keys(this.tableDefaultColumnWidths).forEach(key => {
          const width = saved.columnWidths && saved.columnWidths[key];
          if (validWidth(width)) columnWidths[key] = Math.round(width);
        });
        this.tableSettings = {
          columnWidths,
          rowHeight: ROW_HEIGHTS.includes(saved.rowHeight) ? saved.rowHeight : 16,
          fontSize: FONT_SIZES.includes(saved.fontSize) ? saved.fontSize : 12,
        };
      } catch (error) {
        // Відсутнє, пошкоджене або недоступне сховище не має заважати рендеру.
      }
    },
    persistTableSettings() {
      if (!this.tableSettingsStorageKey) return;
      try {
        window.localStorage.setItem(this.tableSettingsStorageKey, JSON.stringify({
          columnWidths: this.tableColumnWidths,
          rowHeight: this.tableSettings.rowHeight,
          fontSize: this.tableSettings.fontSize,
        }));
      } catch (error) {
        // Налаштування продовжують працювати у цій сесії, навіть якщо сховище заблоковане чи переповнене.
      }
    },
    setTableRowHeight(value) {
      const height = Number(value);
      if (!this.tableSettingsEnabled || !ROW_HEIGHTS.includes(height)) return;
      this.tableSettings.rowHeight = height;
      this.persistTableSettings();
    },
    setTableFontSize(value) {
      const size = Number(value);
      if (!this.tableSettingsEnabled || !FONT_SIZES.includes(size)) return;
      this.tableSettings.fontSize = size;
      this.persistTableSettings();
    },
    resetTableSettings() {
      this.clearTableColumnResize();
      this.tableSettings = defaultSettings();
      if (!this.tableSettingsStorageKey) return;
      try {
        window.localStorage.removeItem(this.tableSettingsStorageKey);
      } catch (error) {
        // Видиму таблицю скидаємо, навіть коли сховище недоступне.
      }
    },
    setTableColumnWidth(key, width) {
      if (!this.tableSettingsEnabled || !Object.prototype.hasOwnProperty.call(this.tableDefaultColumnWidths, key)) return;
      this.$set(this.tableSettings.columnWidths, key,
        Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, Math.round(width))));
    },
    resizeTableColumnByKey(key, delta) {
      this.setTableColumnWidth(key, this.tableColumnWidths[key] + delta);
      this.persistTableSettings();
    },
    startTableColumnResize(event, key) {
      if (!this.tableSettingsEnabled || event.button !== 0 || event.isPrimary === false) return;
      this.clearTableColumnResize();
      this.tableColumnResize = {
        key,
        pointerId: event.pointerId,
        startX: event.clientX,
        startWidth: this.tableColumnWidths[key],
      };
      this._tableResizeDocument = event.currentTarget.ownerDocument;
      this._tableResizeDocument.addEventListener('pointermove', this.moveTableColumnResize);
      this._tableResizeDocument.addEventListener('pointerup', this.finishTableColumnResize);
      this._tableResizeDocument.addEventListener('pointercancel', this.finishTableColumnResize);
      this._tableResizeDocument.defaultView.addEventListener('blur', this.finishTableColumnResize);
    },
    moveTableColumnResize(event) {
      const resize = this.tableColumnResize;
      if (!resize || event.pointerId !== resize.pointerId) return;
      this.setTableColumnWidth(resize.key, resize.startWidth + event.clientX - resize.startX);
    },
    finishTableColumnResize(event) {
      if (!this.tableColumnResize) return;
      if (event && event.pointerId !== undefined && event.pointerId !== this.tableColumnResize.pointerId) return;
      if (event && event.type === 'pointerup') this.moveTableColumnResize(event);
      this.persistTableSettings();
      this.clearTableColumnResize();
    },
    clearTableColumnResize() {
      const doc = this._tableResizeDocument;
      if (doc) {
        doc.removeEventListener('pointermove', this.moveTableColumnResize);
        doc.removeEventListener('pointerup', this.finishTableColumnResize);
        doc.removeEventListener('pointercancel', this.finishTableColumnResize);
        doc.defaultView.removeEventListener('blur', this.finishTableColumnResize);
        this._tableResizeDocument = null;
      }
      this.tableColumnResize = null;
    },
  },
};
